import { TooltipWebV3 } from '@components/TaskList/TooltipWebV3';
import {
  createMaterialTopTabNavigator,
  MaterialTopTabBarProps,
} from '@react-navigation/material-top-tabs';
import { useIsFocused } from '@react-navigation/native';
import * as Clipboard from 'expo-clipboard';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, StyleSheet } from 'react-native';
import Toast from 'react-native-toast-notifications';

import CustomTabBarTeam from '@components/CustomTabBar/CustomTabBarTeam';
import { DetailsTab } from '@components/Profile/TeamDetailPage/DetailsTab.web';
import { InviteMemberModal } from '@components/Profile/TeamDetailPage/InviteMemberModal.web';
import { InvoiceTab } from '@components/Profile/TeamDetailPage/InvoiceTab.web';
import { MembersTab } from '@components/Profile/TeamDetailPage/MembersTab.web';
import { NonMemberContent } from '@components/Profile/TeamDetailPage/NonMemberContent.web';
import { TeamUnavailableContent } from '@components/Profile/TeamDetailPage/TeamUnavailableContent.web';
import { Box, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import { RestyleProps } from '@components/shared/Icon/RestyleIcon';
import { UpgradePaywallTooltip } from '@components/shared/UpgradePaywallTooltip';
import { SimpleHoverable } from '@components/TaskList/SimpleHoverable';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import { useCreateTeamInvitationMutation, Team } from '@graphql/generated';
import { useRefetchOnFocus } from '@hooks/useRefetchOnFocus';
import { useTeamDetailFromQuery } from '@hooks/useTeamDetailFromQuery';
import { useWebDrawerNavigator } from '@hooks/useWebDrawerNavigator';
import { ProfileStackScreenProps } from '@navigation/profile/profile-stack.web';
import { useAppNavigation } from '@navigation/useAppNavigation';
import { SESSION_SELECTED_TEAM_NAME } from '@src/constants/constants';
import theme, { getScaledSize, SCALED_DIMENSIONS } from '@themes/theme';
import {
  APP_STORE_URL,
  DEEP_LINK_TIMEOUT_MS,
  detectAppInstallation,
  isMobileBrowser,
} from '@utils/mobileDeepLink';
import { forgetPendingTeamJoinRequest } from '@utils/pendingTeamJoinRequests';
import { isTeamExpired, shouldShowPaywall } from '@utils/teams/paywall';

const Tab = createMaterialTopTabNavigator();
const TEAM_DETAILS_TAB = 'TeamDetails';
const TEAM_MEMBERS_TAB = 'TeamMembers';

interface CustomButtonProps {
  backgroundColor: RestyleProps['backgroundColor'];
  prefix?: React.ReactNode;
  onPress: () => void;
  disabled?: boolean;
  label: string;
  variant: 'copy' | 'create';
  accessibilityLabel: string;
  borderWidth?: number;
  borderColor?: keyof typeof theme.colors;
  btnTextColor?: keyof typeof theme.colors;
  borderRadius?: keyof typeof theme.borderRadii;
  testID?: string;
  width?: number;
}

const CustomButton: React.FC<CustomButtonProps> = ({
  backgroundColor,
  prefix,
  onPress,
  label,
  disabled,
  variant,
  accessibilityLabel,
  borderWidth,
  borderColor,
  btnTextColor,
  borderRadius,
  testID,
  width,
}) => {
  return (
    <Button
      disabled={disabled}
      backgroundColor={backgroundColor}
      prefix={prefix}
      variant={variant}
      onPress={onPress}
      isSmall
      height={getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT)}
      accessibilityLabel={accessibilityLabel}
      borderWidth={borderWidth}
      borderColor={borderColor}
      btnTextColor={btnTextColor}
      borderRadius={borderRadius}
      width={width}
      testID={testID}>
      {label}
    </Button>
  );
};

/** Context for tab screens so they use a stable component reference and don't remount on parent re-render (e.g. hover). */
type TeamDetailTabContextValue = {
  teamUUId: string;
  displayTeam: Team | undefined;
  isNotTeamMember: boolean;
  isTeamNotFound: boolean;
  hasPendingRequest: boolean;
  publicTeamAsFullTeam: Team | undefined;
};
const TeamDetailTabContext =
  React.createContext<TeamDetailTabContextValue | null>(null);

export function useTeamDetailTabContext() {
  const ctx = useContext(TeamDetailTabContext);
  if (!ctx) throw new Error('TeamDetailTabContext used outside Provider');
  return ctx;
}

/** Stable screen components (module-level) so Tab.Screen does not remount on parent state change (e.g. tooltip hover). */
const TeamDetailsScreen = () => {
  const {
    isNotTeamMember,
    isTeamNotFound,
    teamUUId,
    displayTeam,
    hasPendingRequest,
    publicTeamAsFullTeam,
  } = useTeamDetailTabContext();
  // Before the non-member branch, and before this tab's own skeleton: a team
  // that does not exist is not a membership verdict, and must never offer the
  // join action (TA-10850 QA).
  if (isTeamNotFound) {
    return <TeamUnavailableContent />;
  }
  if (isNotTeamMember) {
    return (
      <NonMemberContent
        teamUUId={teamUUId}
        teamId={displayTeam?.id || publicTeamAsFullTeam?.id}
        hasPendingRequest={hasPendingRequest}
      />
    );
  }
  return <DetailsTab />;
};

const TeamMembersScreen = () => {
  const {
    isNotTeamMember,
    isTeamNotFound,
    teamUUId,
    displayTeam,
    hasPendingRequest,
    publicTeamAsFullTeam,
  } = useTeamDetailTabContext();
  // Before the non-member branch, and before this tab's own skeleton: a team
  // that does not exist is not a membership verdict, and must never offer the
  // join action (TA-10850 QA).
  if (isTeamNotFound) {
    return <TeamUnavailableContent />;
  }
  if (isNotTeamMember) {
    return (
      <NonMemberContent
        teamUUId={teamUUId}
        teamId={displayTeam?.id || publicTeamAsFullTeam?.id}
        hasPendingRequest={hasPendingRequest}
      />
    );
  }
  return <MembersTab />;
};

const TeamInvoiceScreen = () => {
  const {
    isNotTeamMember,
    isTeamNotFound,
    teamUUId,
    displayTeam,
    hasPendingRequest,
    publicTeamAsFullTeam,
  } = useTeamDetailTabContext();
  // Before the non-member branch, and before this tab's own skeleton: a team
  // that does not exist is not a membership verdict, and must never offer the
  // join action (TA-10850 QA).
  if (isTeamNotFound) {
    return <TeamUnavailableContent />;
  }
  if (isNotTeamMember) {
    return (
      <NonMemberContent
        teamUUId={teamUUId}
        teamId={displayTeam?.id || publicTeamAsFullTeam?.id}
        hasPendingRequest={hasPendingRequest}
      />
    );
  }
  return <InvoiceTab />;
};

function checkRemainingDays(targetDateStr: string | number | Date) {
  const targetDate = new Date(targetDateStr);
  const currentDate = new Date();

  const currentUTC = new Date(
    Date.UTC(
      currentDate.getUTCFullYear(),
      currentDate.getUTCMonth(),
      currentDate.getUTCDate()
    )
  );

  const targetUTC = new Date(
    Date.UTC(
      targetDate.getUTCFullYear(),
      targetDate.getUTCMonth(),
      targetDate.getUTCDate()
    )
  );

  const diffInMs = targetUTC.getTime() - currentUTC.getTime();
  const diffInDays = Math.round(diffInMs / (1000 * 60 * 60 * 24));

  if (!isNaN(diffInDays)) return diffInDays;

  return undefined;
}

export const TeamDetailPage = ({
  route,
}: ProfileStackScreenProps<'subscriptionPlanDetail'>) => {
  const { setSelectedTeam, setHeaderTitle } = useWebDrawerNavigator();
  const { t } = useTranslation();
  const navigation = useAppNavigation();

  const {
    params: { teamUUId, openAfterCreate },
  } = route;

  // profile-stack.web keeps inactive screens mounted, so a late not-found
  // answer on a screen the user has already navigated away from must not
  // clear the drawer state that now belongs to another route (TA-10850 QA).
  const isFocused = useIsFocused();

  const isMobile = isMobileBrowser();
  const [bounceComplete, setBounceComplete] = useState(false);

  const toastRef = useRef(null);

  // One query for both cases. getTeam raised "The requested team could not be
  // found" for every invite-link visitor, so membership had to be inferred
  // from that error and a second getPublicTeam round trip. getPublicTeam
  // answers with the Team branch for members and the PublicTeam branch for
  // everyone else, so the branch itself is the membership signal.
  const {
    fullTeam: team,
    team: publicTeamData,
    fullTeam: publicTeamAsFullTeam,
    hasPendingMemberAdditionRequest: hasPendingRequest,
    isNotFound: isNotFoundFromRead,
    loading,
    error: teamError,
    refetch: refetchPublicTeam,
  } = useTeamDetailFromQuery({
    teamUUId,
    fetchPolicy: 'network-only',
    includePending: true,
    // This route owns the selection, so it is the one call site allowed to
    // drop the persisted team name when the team turns out to be gone — and
    // only while it is the focused one.
    shouldClearSelectedTeamOnNotFound: isFocused,
  });

  // `isNotFound` is a verdict about the team, not about membership: the uuid
  // is stale or the team was deleted. TA-10850 QA: routing it into the
  // non-member state offered "Request to Join" and submitted a join request
  // against a uuid the backend has already rejected, so it gets its own
  // unavailable screen with no action. The hook derives it through
  // isApolloResourceNotFound, because the backend answers with HTTP 401 and
  // Apollo hides the code and wording under networkError.result for a
  // non-2xx.

  // The other way a team turns out not to exist: the query settles cleanly —
  // no error — with neither branch of the union. The hook normalizes a null
  // or unrecognized answer to `team === undefined`, and DrawerHeader already
  // reads that as a missing team. Without it this route called the same
  // response a non-member and offered "Request to Join" for an empty or
  // stale uuid (TA-10850 QA).
  const isTeamSettledWithoutBranch =
    !!teamUUId && !loading && !teamError && !publicTeamData;

  const isTeamNotFound = isNotFoundFromRead || isTeamSettledWithoutBranch;

  // A failed read is not a membership verdict either. An offline blip or 5xx
  // settles loading to false with no team, which is indistinguishable from an
  // invite-link visitor, and would otherwise show a real member the
  // join-request screen with an enabled action.
  const isTeamReadFailed = !!teamError && !isTeamNotFound;

  // `== null` rather than `=== undefined`: Team.currentUserRole is
  // Maybe<TeamRole>, so the Team branch returned for a pending/non-member
  // viewer can carry an explicit null, which a strict undefined check reads
  // as "has a role" and lets straight through the gate.
  const isNotTeamMember =
    !loading &&
    !isTeamReadFailed &&
    !isTeamNotFound &&
    (team == undefined || team?.currentUserRole == null);

  useEffect(() => {
    if (!isMobile || loading || isNotTeamMember || isTeamNotFound) return;
    const deepLinkUrl = `com.eloveit.tasktag:///my-account/subscriptionPlanDetail/${teamUUId}/TeamDetails`;
    const cleanup = detectAppInstallation(deepLinkUrl, APP_STORE_URL);
    const completeTimer = setTimeout(
      () => setBounceComplete(true),
      DEEP_LINK_TIMEOUT_MS + 100
    );
    return () => {
      cleanup();
      clearTimeout(completeTimer);
    };
  }, [teamUUId, isMobile, loading, isNotTeamMember, isTeamNotFound]);

  // TA-10772: an approval (or a request made in another tab) lands while this
  // screen is already mounted, and nothing else here would notice. Guarded
  // because the query is skipped for members — refetching while skipped would
  // fire with an empty id. Mirrors TeamDetails.tsx on native.
  // A failed read refetches too (TA-10850 QA): it deliberately no longer
  // counts as a non-member, so without this an offline blip or 5xx left the
  // page stuck in the failed state for as long as it stayed mounted — coming
  // back to it never retried.
  // The not-found state refetches too (TA-10850 review): it is not always a
  // permanent verdict — `isTeamSettledWithoutBranch` also sets it for a clean
  // `getPublicTeam: null`, which a link opened while an invite or a team is
  // still provisioning answers with. Without this the tab sat on "Team not
  // found" with every control disabled until a full page reload. A genuinely
  // deleted team just answers not-found again.
  const refetchPublicTeamOnFocus = useCallback(
    () =>
      isNotTeamMember || isTeamReadFailed || isTeamNotFound
        ? refetchPublicTeam()
        : Promise.resolve(),
    [isNotTeamMember, isTeamReadFailed, isTeamNotFound, refetchPublicTeam]
  );
  useRefetchOnFocus(refetchPublicTeamOnFocus, 0);

  // Use public team data if user is not a member, otherwise use regular team data
  let displayTeam: Team | undefined;
  if (isNotTeamMember && publicTeamAsFullTeam) {
    displayTeam = publicTeamAsFullTeam;
  } else if (isNotTeamMember && publicTeamData?.__typename === 'PublicTeam') {
    // Use public team name and address for display (header, details); other Team fields stay undefined
    displayTeam = {
      id: publicTeamData.uuid,
      uuid: publicTeamData.uuid,
      name: publicTeamData.name,
      address: publicTeamData.address,
      avatarFile: publicTeamData.avatarFile,
    } as Team;
  } else if (isNotTeamMember && publicTeamData) {
    displayTeam = undefined;
  } else {
    displayTeam = team;
  }

  // TA-10772: the join request is resolved once getTeam answers with a role,
  // so drop the local "pending" record written when it was created. Without
  // this a user who is approved and later leaves the team would still be shown
  // a disabled "Pending Request" button for the rest of the tab session.
  useEffect(() => {
    // Only a read that confirmed membership may drop the record. Neither a
    // not-found team nor a failed read is such a confirmation, and both leave
    // `isNotTeamMember` false — so gate on them explicitly, matching the
    // membership gate above (TA-10850 QA). Forgetting on a transient failure
    // would offer "Request to Join" again after the next successful read.
    if (!loading && !isNotTeamMember && !isTeamNotFound && !isTeamReadFailed) {
      forgetPendingTeamJoinRequest(teamUUId);
    }
  }, [loading, isNotTeamMember, isTeamNotFound, isTeamReadFailed, teamUUId]);

  const isLoading = loading;

  const { fullTeam: cacheTeam } = useTeamDetailFromQuery({
    teamUUId,
    fetchPolicy: 'cache-only',
    // Must match the network-only call above, or this reads a different
    // cache entry and never sees that query's result.
    includePending: true,
  });

  useEffect(() => {
    if (displayTeam) {
      setSelectedTeam(displayTeam);
    }
  }, [
    isNotTeamMember,
    cacheTeam?.name,
    displayTeam?.name,
    displayTeam?.avatarFile,
    displayTeam?.usersCount,
    displayTeam?.users?.length,
    displayTeam?.currentUserRole,
    displayTeam?.owner?.id,
  ]);

  // TA-10850 QA: DrawerHeader deliberately skips its own team cleanup on this
  // route, so a stale or deleted link opened while another team was selected
  // left that team's name and controls in the drawer beside the unavailable
  // screen. This is where the route that owns the selection is known.
  //
  // The persisted key is cleared here too, not only by the hook: the hook
  // acts on its own error-based verdict, so a clean `getPublicTeam: null`
  // would leave the name behind, and a leftover key still selects the
  // subscription-header branch — masking the "Team Not Found" title.
  useEffect(() => {
    if (!isTeamNotFound || !isFocused) return;

    setSelectedTeam(undefined);
    // Best-effort: the `sessionStorage` getter itself throws in Safari
    // private mode and wherever storage is blocked.
    try {
      window.sessionStorage?.removeItem(SESSION_SELECTED_TEAM_NAME);
    } catch {
      // Nothing to clean up if the key was never readable to begin with.
    }
  }, [isTeamNotFound, isFocused, setSelectedTeam]);

  // Set drawer header title to team name (member or public data) so it shows instead of "My Account"
  // A team that does not exist still gets a title, rather than falling back
  // to "My Account": the header has to say why the page is empty.
  const teamNameForHeader = isTeamNotFound
    ? t('models:teams.notFoundTitle')
    : (displayTeam?.name ??
      (publicTeamData?.__typename === 'PublicTeam'
        ? publicTeamData.name
        : undefined));
  // Setting and clearing are separate effects on purpose (TA-10850 QA).
  //
  // Setting is focus-gated: a mounted-but-inactive copy of this screen — the
  // web stacks keep them — must not push its title over the active route's.
  // The ref records that this screen is the one whose title is on screen; it
  // gives that up on blur, when another route takes over.
  const ownsHeaderTitleRef = useRef(false);
  useEffect(() => {
    if (!isFocused) {
      ownsHeaderTitleRef.current = false;
      return;
    }
    if (teamNameForHeader) {
      setHeaderTitle(teamNameForHeader);
      ownsHeaderTitleRef.current = true;
    }
  }, [teamNameForHeader, isFocused, setHeaderTitle]);

  // Clearing happens on unmount only, never on blur: at blur time the newly
  // focused route has already installed its own title, and clearing from here
  // would erase it. The ownership ref carries that further — an inactive copy
  // of this screen unmounted long after another route took over owns nothing
  // and must stay out of it.
  useEffect(
    () => () => {
      if (ownsHeaderTitleRef.current && !navigation.isFocused()) {
        setHeaderTitle(undefined);
      }
    },
    []
  );

  // Every control on this screen acts on a team. A non-member may not use
  // them, and for a team that does not exist there is nothing to act on — so
  // both states disable the same set (TA-10850 QA).
  const areTeamActionsDisabled = isNotTeamMember || isTeamNotFound;

  const isTeamMember = team?.currentUserRole === 'MEMBER';
  const isTeamOwnerOrAdmin =
    team?.currentUserRole === 'OWNER' || team?.currentUserRole === 'ADMIN';
  const isPersonalTeam = displayTeam?.personal ?? team?.personal ?? false;
  const teamMemberCount =
    displayTeam?.usersCount ??
    displayTeam?.users?.length ??
    team?.usersCount ??
    0;
  const initialTabName =
    openAfterCreate && teamMemberCount <= 1
      ? TEAM_MEMBERS_TAB
      : TEAM_DETAILS_TAB;

  const [isExpired, setIsexpired] = useState(false);
  useEffect(() => {
    // Only check subscription details if we have a full team (not public team)
    const fullTeam = displayTeam || team;
    const dateRemaining = checkRemainingDays(
      fullTeam?.subscriptionDetails?.expirationDate
    );

    if (
      fullTeam?.subscriptionDetails?.isSubscriptionCanceled &&
      dateRemaining &&
      dateRemaining <= 0
    ) {
      setIsexpired(true);
      return;
    }
    if (
      fullTeam?.subscriptionDetails?.expirationDate &&
      dateRemaining &&
      dateRemaining <= 7 &&
      (fullTeam?.subscriptionDetails?.paymentCardLast4 === null ||
        fullTeam?.subscriptionDetails?.paymentCardLast4 === undefined)
    ) {
      setIsexpired(true);
    }
  }, [
    displayTeam?.subscriptionDetails?.expirationDate,
    displayTeam?.subscriptionDetails?.isSubscriptionCanceled,
    team?.subscriptionDetails?.expirationDate,
    team?.subscriptionDetails?.isSubscriptionCanceled,
  ]);

  const invoiceDotColor: keyof typeof theme.colors | undefined = (() => {
    if (isPersonalTeam) return undefined;
    const fullTeam = displayTeam || team;
    if (isTeamExpired(fullTeam)) return 'alertRed';
    if (fullTeam?.subscriptionDetails?.isSubscriptionOnTrial)
      return 'yellowVivid';
    return undefined;
  })();

  // Tooltip state machine:
  // - 'hover'  : show "Copy Link to Invite"
  // - 'copied' : show "Link copied!"
  // - null     : hidden
  const [tooltipMode, setTooltipMode] = useState<'hover' | 'copied' | null>(
    null
  );

  // Visual hover state for the link icon (driven by SimpleHoverable)
  const [isLinkIconHovered, setIsLinkIconHovered] = useState(false);

  // When we just showed "copied", we don't want hover tooltip to re-appear
  // until the mouse actually leaves and re-enters.
  const [suppressHoverTooltip, setSuppressHoverTooltip] = useState(false);

  // Sync link icon hover state to tooltip mode
  useEffect(() => {
    if (isLinkIconHovered && !suppressHoverTooltip && !areTeamActionsDisabled) {
      setTooltipMode('hover');
    } else if (!isLinkIconHovered && tooltipMode === 'hover') {
      setTooltipMode(null);
    }
    // Always clear suppressHoverTooltip on hover-out to allow hover tooltips again
    if (!isLinkIconHovered) {
      setSuppressHoverTooltip(false);
    }
  }, [
    isLinkIconHovered,
    suppressHoverTooltip,
    areTeamActionsDisabled,
    tooltipMode,
  ]);

  // Keep a timer ref to avoid setState after unmount / repeated clicks.
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (copiedTimerRef.current) {
        clearTimeout(copiedTimerRef.current);
        copiedTimerRef.current = null;
      }
    };
  }, []);

  // Copy link to clipboard and show "Link copied!" tooltip.
  const copyToClipboard = useCallback(async (link: string) => {
    if (!link) return;
    try {
      await Clipboard.setStringAsync(link);
    } catch {
      return;
    }

    // Force "copied" mode and block hover mode until mouse leaves.
    setSuppressHoverTooltip(true);
    setTooltipMode('copied');

    // Reset timer if user clicks multiple times quickly.
    if (copiedTimerRef.current) {
      clearTimeout(copiedTimerRef.current);
    }

    copiedTimerRef.current = setTimeout(() => {
      // IMPORTANT:
      // Close tooltip directly instead of switching back to hover title,
      // to avoid flashing during Popper fade-out transition.
      setTooltipMode(null);
      // Do NOT setSuppressHoverTooltip(false) here.
      // We unlock it only on actual mouse leave.
    }, 3000);
  }, []);

  const { setPaywallModalOpen, setPaywallTeam } = useWebDrawer();

  const [
    createTeamInvitation,
    { loading: copyLinkLoading, data: createTeamInvitationData },
  ] = useCreateTeamInvitationMutation();

  useEffect(() => {
    const raw = createTeamInvitationData?.createTeamInvitation;
    if (!raw) return;
    const tokenLink =
      raw.replace('register/register-with-email', 'invited-user') +
      '&type=team';
    copyToClipboard(tokenLink);
  }, [createTeamInvitationData]);

  const handleInviteToTeamPress = async () => {
    if (shouldShowPaywall(team)) {
      setPaywallTeam(team ?? undefined);
      setPaywallModalOpen(true);
      return;
    }
    const teamId = displayTeam?.uuid ?? team?.uuid;
    if (!teamId || copyLinkLoading) return;
    try {
      await createTeamInvitation({ variables: { id: teamId } });
    } catch {
      // Fail silently — user will see no feedback, which is acceptable
    }
  };

  const [isInviteModalVisible, setIsInviteModalVisible] = useState(false);

  // Reset, not just hide: leaving the flag true would reopen the modal without
  // a user action if this route recovers or is reused for another team, and it
  // would then act on that team's uuid (TA-10850 QA).
  useEffect(() => {
    if (areTeamActionsDisabled && isInviteModalVisible) {
      setIsInviteModalVisible(false);
    }
  }, [areTeamActionsDisabled, isInviteModalVisible]);

  const tabContextValue = useMemo<TeamDetailTabContextValue>(
    () => ({
      teamUUId: teamUUId ?? '',
      displayTeam,
      isNotTeamMember,
      isTeamNotFound,
      hasPendingRequest,
      publicTeamAsFullTeam: publicTeamAsFullTeam ?? undefined,
    }),
    [
      teamUUId,
      displayTeam,
      isNotTeamMember,
      isTeamNotFound,
      hasPendingRequest,
      publicTeamAsFullTeam,
    ]
  );

  const renderTabBar = (props: MaterialTopTabBarProps) => {
    return (
      <Box marginVertical='m'>
        <Box
          flexDirection='row'
          alignItems='flex-end'
          justifyContent='space-between'
          pb='none'
          paddingHorizontal='xs'
          borderBottomWidth={1}
          borderBottomColor='grey02'
          style={styles.topShadow}>
          <Box
            flexDirection='row'
            alignItems='flex-end'
            marginHorizontal='s'
            pb='none'
            style={styles.topShadow}>
            <CustomTabBarTeam
              {...props}
              spacing={theme.spacing.s}
              activeMemberCount={displayTeam?.usersCount ?? 0}
              invoiceCount={displayTeam?.invoices?.length ?? 0}
              disabled={areTeamActionsDisabled}
              invoiceDotColor={invoiceDotColor}
            />
          </Box>
          <Box flexDirection='row' alignItems='center' mb='s'>
            {!isPersonalTeam && (
              <>
                <Box
                  marginRight='xs'
                  height={32}
                  width={32}
                  alignItems='center'
                  justifyContent='center'
                  position='relative'>
                  <TooltipWebV3
                    marginOffset={8}
                    open={tooltipMode !== null && !areTeamActionsDisabled}
                    disableHoverListener
                    disableFocusListener
                    disableTouchListener
                    placement='bottom-end'
                    variant={tooltipMode === 'hover' ? 'dark' : 'light'}
                    title={
                      tooltipMode === 'copied' ? (
                        <Box flexDirection='row'>
                          <Icon
                            name='CheckCircle'
                            color='greenSecondary'
                            variant='s'
                          />
                          <Text ml='xs' variant='labelSmall' color='black'>
                            Link copied!
                          </Text>
                        </Box>
                      ) : (
                        <Text ml='xxs' variant='labelSmall' color='white'>
                          {copyLinkTooltipLabel}
                        </Text>
                      )
                    }>
                    <SimpleHoverable
                      testID='copy-link-icon'
                      accessibilityLabel={t('shared:copyLinkToInvite')}
                      accessibilityRole='button'
                      disabled={areTeamActionsDisabled || copyLinkLoading}
                      onPress={
                        areTeamActionsDisabled || copyLinkLoading
                          ? undefined
                          : handleInviteToTeamPress
                      }
                      onHoverIn={() => setIsLinkIconHovered(true)}
                      onHoverOut={() => setIsLinkIconHovered(false)}>
                      {(isHovered) => (
                        <Box
                          width={32}
                          height={32}
                          alignItems='center'
                          justifyContent='center'
                          borderRadius='xs'
                          backgroundColor={
                            isHovered && !areTeamActionsDisabled
                              ? 'grey01'
                              : 'transparent'
                          }
                          style={{
                            opacity: areTeamActionsDisabled ? 0.5 : 1,
                            pointerEvents: areTeamActionsDisabled
                              ? 'none'
                              : 'auto',
                          }}>
                          <Icon
                            height={24}
                            width={24}
                            name='Link'
                            color='black'
                          />
                        </Box>
                      )}
                    </SimpleHoverable>
                  </TooltipWebV3>
                </Box>
                {(isTeamMember || isTeamOwnerOrAdmin) && (
                  <Box width={122} mr='xs'>
                    {shouldShowPaywall(team) ? (
                      <UpgradePaywallTooltip show={true}>
                        <div>
                          <CustomButton
                            width={122}
                            disabled={areTeamActionsDisabled}
                            backgroundColor='white'
                            variant='create'
                            borderWidth={1}
                            borderColor='black'
                            btnTextColor='black'
                            borderRadius='m'
                            prefix={
                              <Icon variant='s' name='UserPlus' color='black' />
                            }
                            onPress={() => {
                              setPaywallTeam(team ?? undefined);
                              setPaywallModalOpen(true);
                            }}
                            label={t('shared:invite')}
                            accessibilityLabel={t('shared:addMembers')}
                            testID='invite-member-button'
                          />
                        </div>
                      </UpgradePaywallTooltip>
                    ) : (
                      <TooltipWebV3
                        title={
                          <Text ml='xxs' variant='labelSmall' color='white'>
                            Invite member to this team
                          </Text>
                        }
                        placement='bottom-end'>
                        <SimpleHoverable
                          disabled={isExpired || areTeamActionsDisabled}
                          onPress={() => {
                            if (!isExpired && !areTeamActionsDisabled) {
                              setIsInviteModalVisible(true);
                            }
                          }}
                          accessibilityLabel={t('shared:addMembers')}
                          testID='invite-member-button'>
                          {(isHovered) => (
                            <Box
                              flexDirection='row'
                              alignItems='center'
                              justifyContent='center'
                              width={122}
                              height={getScaledSize(
                                SCALED_DIMENSIONS.BUTTON_HEIGHT
                              )}
                              paddingHorizontal='s'
                              borderRadius='m'
                              borderWidth={1}
                              borderColor={
                                isExpired || areTeamActionsDisabled
                                  ? 'grey04'
                                  : 'black'
                              }
                              backgroundColor={
                                isHovered &&
                                !isExpired &&
                                !areTeamActionsDisabled
                                  ? 'grey01'
                                  : 'white'
                              }>
                              <Icon
                                variant='s'
                                name='UserPlus'
                                color={
                                  isExpired || areTeamActionsDisabled
                                    ? 'grey04'
                                    : 'black'
                                }
                              />
                              <Text
                                variant='labelSmall'
                                color={
                                  isExpired || areTeamActionsDisabled
                                    ? 'grey04'
                                    : 'black'
                                }
                                ml='xxs'>
                                {t('shared:invite')}
                              </Text>
                            </Box>
                          )}
                        </SimpleHoverable>
                      </TooltipWebV3>
                    )}
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  // Not for a team that does not exist: there is no app screen to bounce
  // into, and the spinner would hide the reason the page is empty.
  if (isMobile && !isLoading && !isNotTeamMember && !isTeamNotFound) {
    return (
      <Box
        flex={1}
        backgroundColor='white'
        justifyContent='center'
        alignItems='center'>
        {!bounceComplete && <ActivityIndicator size='large' />}
      </Box>
    );
  }

  // TA-10280: the header + tab bar render immediately instead of blanking
  // behind a full-page spinner — each tab body (Overview/Members/Invoice)
  // gates its own skeleton on its own query via `useWebDrawerNavigator()`'s
  // `selectedTeam`, so `isLoading` no longer needs to hide them.

  let copyLinkTooltipLabel = 'Copy Link to Invite';
  if (shouldShowPaywall(team)) {
    copyLinkTooltipLabel = 'Upgrade to unlock team features';
  } else if (copyLinkLoading) {
    copyLinkTooltipLabel = 'Getting link…';
  }

  return (
    <Box flex={1} zIndex={100}>
      <Toast ref={toastRef} />
      <TeamDetailTabContext.Provider value={tabContextValue}>
        <Tab.Navigator
          screenOptions={{
            swipeEnabled: false,
          }}
          initialRouteName={initialTabName}
          backBehavior='none'
          tabBar={renderTabBar}>
          <Tab.Screen
            name={TEAM_DETAILS_TAB}
            options={{
              tabBarLabel: `Overview`,
            }}
            component={TeamDetailsScreen}
          />
          <Tab.Screen
            name={TEAM_MEMBERS_TAB}
            options={{
              tabBarLabel: `Members`,
            }}
            component={TeamMembersScreen}
          />
          {!isPersonalTeam && (
            <Tab.Screen
              name='TeamInvoice'
              options={{
                tabBarLabel: `Invoice`,
              }}
              component={TeamInvoiceScreen}
            />
          )}
        </Tab.Navigator>
      </TeamDetailTabContext.Provider>
      <InviteMemberModal
        // Belt and braces with the reset effect above: an open modal must not
        // survive one render with Send / Copy live against a stale uuid.
        isVisible={isInviteModalVisible && !areTeamActionsDisabled}
        onClose={() => setIsInviteModalVisible(false)}
        teamId={displayTeam?.uuid || ''}
      />
    </Box>
  );
};

const styles = StyleSheet.create({
  topShadow: {
    backgroundColor: 'white',
  },
  contentContainerPanelStyle: {
    paddingHorizontal: 24,
    // paddingVertical: 4,
    backgroundColor: '#ffffff',
  },
});
